import React, { useState } from 'react';
import { useWallets } from '../WalletContext';
import './MultiWalletSelector.css';

function MultiWalletSelector() {
  const { wallets, selectedWallets, handleWalletToggle, getSelectedWalletsDisplay, getCurrencySymbol } = useWallets();
  const [isOpen, setIsOpen] = useState(false);

  // Filter out "All Pots" option (id=0)
  const selectableWallets = wallets.filter(w => w.id !== 0);

  // Auto-open if no pot is selected and we have pots
  React.useEffect(() => {
    if (selectableWallets.length > 0 && selectedWallets.length === 0) {
      console.log('Auto-opening pot selector - no pot selected');
      setIsOpen(true);
    }
  }, [selectableWallets.length, selectedWallets.length]);

  const isSelected = (walletId) => {
    return selectedWallets.includes(walletId);
  };

  const canSelectWallet = (walletId) => {
    // If pot is already selected, it can be deselected
    if (selectedWallets.includes(walletId)) return true;
    
    // If no pots selected, any can be selected
    if (selectedWallets.length === 0) return true;
    
    // Different currency pots are selectable (will auto-switch)
    // Same currency pots can be multi-selected
    return true;
  };

  const getWalletTooltip = (walletId, walletCurrency) => {
    if (selectedWallets.includes(walletId)) return 'Click to deselect';
    if (selectedWallets.length === 0) return 'Click to select';
    
    const selectedCurrency = selectableWallets.find(w => w.id === selectedWallets[0])?.currency;
    if (selectedCurrency && walletCurrency !== selectedCurrency) {
      return `Click to switch to ${walletCurrency} (will deselect other pots)`;
    }
    return 'Click to add to selection';
  };

  const handleClick = (walletId) => {
    handleWalletToggle(walletId);
  };

  return (
    <div className="multi-wallet-selector">
      <div className="wallet-selector-header" onClick={() => setIsOpen(!isOpen)}>
        <div className="selected-display">
          <span className="wallet-icon">🫙</span>
          <div className="selected-text">
            <div className="selected-label">Selected Pots</div>
            <div className="selected-value">{getSelectedWalletsDisplay()}</div>
          </div>
        </div>
        <span className={`dropdown-arrow ${isOpen ? 'open' : ''}`}>▼</span>
      </div>

      {isOpen && (
        <div className="wallet-dropdown">
          {selectableWallets.length === 0 ? (
            <div className="no-wallets-message">
              <p>No pots found!</p>
              <p>Go to Settings → Pots to create one.</p>
            </div>
          ) : (
            <>
              {selectableWallets.map(wallet => {
                const selectable = canSelectWallet(wallet.id);
                return (
                  <div 
                    key={wallet.id}
                    className={`wallet-option ${isSelected(wallet.id) ? 'selected' : ''}`}
                    onClick={() => handleClick(wallet.id)}
                    title={getWalletTooltip(wallet.id, wallet.currency)}
                  >
                    <input 
                      type="checkbox" 
                      checked={isSelected(wallet.id)}
                      readOnly
                    />
                    <span className="wallet-option-icon">{wallet.icon || '🫙'}</span>
                    <div className="wallet-option-info">
                      <div className="wallet-option-name">{wallet.name}</div>
                      <div className="wallet-option-currency">
                        {getCurrencySymbol(wallet.currency)} {wallet.currency}
                      </div>
                    </div>
                  </div>
                );
              })}

              <div className="wallet-hint">
                💡 Click any pot to switch currencies or add pots with same currency
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default MultiWalletSelector;
